import React from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import Icon from '../../../components/AppIcon';

const FinancialSummaryCards = ({ summaryData }) => {
  const { currentLanguage, t } = useLanguage();

  const formatCurrency = (amount) => {
    if (currentLanguage === 'ar') {
      // Arabic formatting with Arabic numerals
      const formattedNumber = new Intl.NumberFormat('ar-MA', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })?.format(amount);
      return `${formattedNumber} ${t('financial.currency')}`;
    } else {
      // French formatting
      return new Intl.NumberFormat('fr-MA', {
        style: 'currency',
        currency: 'MAD',
        minimumFractionDigits: 2
      })?.format(amount);
    }
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) return 'TrendingUp';
    if (trend < 0) return 'TrendingDown';
    return 'Minus';
  };

  const getTrendColor = (trend, isPositive = true) => {
    if (trend === 0) return 'text-muted-foreground';
    if (isPositive) {
      return trend > 0 ? 'text-success' : 'text-error';
    }
    return trend > 0 ? 'text-error' : 'text-success';
  };

  const getLabel = (labelKey) => {
    const labelMap = {
      totalRevenue: 'financial.totalRevenue',
      outstandingBalance: 'financial.outstandingBalance',
      refundsProcessing: 'financial.refundsProcessing',
      groupProfitability: 'financial.groupProfitability'
    };
    return t(labelMap?.[labelKey] || labelKey);
  };

  const getComparisonLabel = (comparisonKey) => {
    const comparisonMap = {
      vsLastMonth: 'financial.vsLastMonth',
      vsLastQuarter: 'financial.vsLastQuarter'
    };
    return t(comparisonMap?.[comparisonKey] || comparisonKey);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
      {summaryData?.map((card, index) => (
        <div
          key={index}
          className="bg-card rounded-lg p-4 md:p-6 border border-border shadow-warm hover:shadow-warm-md transition-smooth"
        >
          <div className="flex items-start justify-between mb-4">
            <div className={`w-12 h-12 rounded-lg ${card?.bgColor} flex items-center justify-center`}>
              <Icon name={card?.icon} size={24} className={card?.iconColor} />
            </div>
            <div className={`flex items-center gap-1 caption ${getTrendColor(card?.trend, card?.isPositiveTrend)}`}>
              <Icon name={getTrendIcon(card?.trend)} size={16} />
              <span className="font-medium">{Math.abs(card?.trend)}%</span>
            </div>
          </div>
          
          <h3 className="caption text-muted-foreground mb-2">
            {getLabel(card?.labelKey)}
          </h3>
          
          <p className="text-2xl md:text-3xl font-semibold text-foreground mb-2 data-text">
            {formatCurrency(card?.amount)}
          </p>
          
          <p className="caption text-muted-foreground">
            {getComparisonLabel(card?.comparisonKey)}
          </p>
        </div>
      ))}
    </div>
  );
};

export default FinancialSummaryCards;